import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Package, Warehouse, Factory, ChevronsUpDown, Check, Paperclip, FileImage, FileText as FileTextIcon, Download, X as XIcon, FileSpreadsheet, Upload } from "lucide-react";
import { fmtCurrency } from "@/utils/format";

export default function QuoteForm() {
  const { id } = useParams();
  const location = useLocation();
  // When the URL sits under /siparisler/:id/duzenle we're editing an order,
  // otherwise it's a quote flow (new or edit).
  const isOrderEdit = location.pathname.startsWith("/siparisler/");
  const isEdit = !!id;
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [openProductRow, setOpenProductRow] = useState(null); // index of item whose product combobox is open
  // Refs used by the "auto-save-as-draft on unmount" flow — we need latest
  // form state inside an effect cleanup that only runs once.
  const savedRef = useRef(false);
  const formRef = useRef(null);
  const contextRef = useRef({ isEdit: false, isOrderEdit: false });
  const [form, setForm] = useState({
    customer_id: "",
    tax_rate: 20,
    notes: "",
    payment_info: "",
    items: [], // {product_id, quantity, unit_price, due_date, notes}
    attachments: [], // {name, content_type, size, data_b64?, existing?, idx?}
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, p, s] = await Promise.all([
          api.get("/customers"),
          api.get("/products"),
          api.get("/settings/payment-options"),
        ]);
        setCustomers(c.data); setProducts(p.data);
        setPaymentOptions(s.data?.payment_options || []);
        if (isEdit) {
          const endpoint = isOrderEdit ? `/orders/${id}` : `/quotes/${id}`;
          const { data } = await api.get(endpoint);
          setForm({
            customer_id: data.customer_id,
            tax_rate: data.tax_rate,
            notes: data.notes || "",
            payment_info: data.payment_info || "",
            items: (data.items || [])
              .filter((it) => !isOrderEdit || (it.status !== "shipped" && it.status !== "cancelled"))
              .map((it) => ({
                id: it.id,
                product_id: it.product_id,
                quantity: it.quantity,
                unit_price: it.unit_price,
                due_date: it.due_date || "",
                notes: it.notes || "",
              })),
            // Attachments come back as metadata-only (name/content_type/size).
            // We keep them marked `existing` so we send them back unchanged.
            attachments: (data.attachments || []).map((a, idx) => ({
              ...a, existing: true, idx,
            })),
          });
        }
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [id, isEdit, isOrderEdit]);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", quantity: 1, unit_price: 0, due_date: "", notes: "" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, patch) => {
    const items = [...form.items];
    items[i] = { ...items[i], ...patch };
    if (patch.product_id) {
      const p = products.find((x) => x.id === patch.product_id);
      if (p && !items[i].unit_price) items[i].unit_price = p.price ?? 0;
    }
    setForm({ ...form, items });
  };

  // -------- Excel import --------
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const downloadImportTemplate = async () => {
    try {
      const res = await api.get("/quotes/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "teklif_sablonu.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const itemsInputRef = useRef(null);
  const [itemsUploading, setItemsUploading] = useState(false);

  const downloadItemsExcel = async () => {
    try {
      const res = await api.get(`/quotes/${id}/items-excel`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const m = /filename\*=UTF-8''([^;]+)/.exec(res.headers?.["content-disposition"] || "");
      a.href = url; a.download = m ? decodeURIComponent(m[1]) : "teklif - kalemler.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Excel indirilemedi"); }
  };

  const handleItemsFile = async (fileList) => {
    const f = (fileList || [])[0];
    if (itemsInputRef.current) itemsInputRef.current.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) { toast.error("Yalnızca .xlsx dosyaları kabul edilir"); return; }
    if (!window.confirm("Mevcut kalemler güncellenecek, devam edilsin mi?")) return;
    const fd = new FormData();
    fd.append("file", f);
    setItemsUploading(true);
    try {
      const { data } = await api.post("/quotes/items-excel/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => {
        const old = Object.fromEntries(prev.items.filter((it) => it.id).map((it) => [it.id, it]));
        return {
          ...prev,
          items: data.items.map((it) => {
            const o = it.id && old[it.id] && old[it.id].product_id === it.product_id ? old[it.id] : null;
            return { id: o ? o.id : undefined, product_id: it.product_id, quantity: it.quantity, unit_price: it.unit_price,
              due_date: o?.due_date || "", notes: o?.notes || "" };
          }),
        };
      });
      toast.success(`${data.items.length} kalem güncellendi — kaydetmeyi unutmayın`);
      if (data.warnings?.length) toast.warning(`Eşleşmeyen / atlanan satırlar:\n${data.warnings.slice(0, 8).join("\n")}`, { duration: 12000 });
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.message ? `${det.message}\n${(det.warnings || []).slice(0, 8).join("\n")}` : (formatApiErrorDetail(det) || "Yükleme başarısız"), { duration: 12000 });
    } finally {
      setItemsUploading(false);
    }
  };

  const handleImportFile = async (fileList) => {
    const f = (fileList || [])[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Yalnızca .xlsx dosyaları kabul edilir");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setImporting(true);
    try {
      const { data } = await api.post("/quotes/import-parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({
        ...prev,
        customer_id: data.customer_id || prev.customer_id,
        tax_rate: data.tax_rate ?? prev.tax_rate,
        notes: data.notes || prev.notes,
        items: (data.items || []).map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          unit_price: it.unit_price,
          due_date: it.due_date || "",
          notes: it.notes || "",
        })),
      }));
      toast.success(`${(data.items || []).length} kalem içeri aktarıldı`);
      (data.warnings || []).slice(0, 5).forEach((w) => toast.warning(w));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İçeri aktarım başarısız");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const ALLOWED_MIME = ["application/pdf", "image/png", "image/jpeg"];
  const MAX_FILES = 5;
  const MAX_BYTES = 5 * 1024 * 1024;

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const addAttachments = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (form.attachments.length + arr.length > MAX_FILES) {
      toast.error(`En fazla ${MAX_FILES} dosya eklenebilir`);
      return;
    }
    const next = [...form.attachments];
    for (const f of arr) {
      if (!ALLOWED_MIME.includes(f.type)) {
        toast.error(`'${f.name}' desteklenmiyor. Sadece PDF, PNG, JPG.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`'${f.name}' çok büyük (max 5 MB)`);
        continue;
      }
      try {
        const b64 = await readAsBase64(f);
        next.push({ name: f.name, content_type: f.type, size: f.size, data_b64: b64 });
      } catch { toast.error(`'${f.name}' okunamadı`); }
    }
    setForm((prev) => ({ ...prev, attachments: next }));
  };

  const removeAttachment = (idx) => {
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
  };

  const downloadExisting = async (att) => {
    try {
      const res = await api.get(`/quotes/${id}/attachments/${att.idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.name || `ek-${att.idx}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const taxAmount = (subtotal * Number(form.tax_rate || 0)) / 100;
  const total = subtotal + taxAmount;

  // Keep refs in sync with the latest render so the unmount cleanup below
  // can inspect the current form even though it's outside React state flow.
  formRef.current = form;
  contextRef.current = { isEdit, isOrderEdit };

  // Auto-save-as-draft when the user leaves the "new quote" screen without
  // clicking Kaydet. Runs on unmount (in-app navigation) AND on beforeunload
  // (tab close / hard reload) via a keepalive `fetch` so the request survives.
  //   • Only for NEW quotes (not edits, not order edits)
  //   • Only when the user actually filled something in
  //   • Only when a customer is selected (backend requires customer_id)
  //   • Silently skipped when the user just successfully saved (savedRef=true)
  useEffect(() => {
    const buildDraftPayload = () => {
      const f = formRef.current;
      if (!f) return null;
      return {
        customer_id: f.customer_id,
        tax_rate: Number(f.tax_rate) || 20,
        notes: f.notes || "",
        payment_info: (f.payment_info || "").trim(),
        items: (f.items || []).map((it) => ({
          id: it.id || undefined,
          product_id: it.product_id,
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          due_date: (it.due_date || "").trim() || null,
          notes: (it.notes || "").trim(),
        })).filter((it) => it.product_id),
        attachments: (f.attachments || []).map((a) => (
          a.existing
            ? { keep_idx: a.idx }
            : { name: a.name, content_type: a.content_type, data_b64: a.data_b64 }
        )),
      };
    };

    const shouldAutoSave = () => {
      if (savedRef.current) return false;
      const { isEdit: e, isOrderEdit: oe } = contextRef.current;
      if (e || oe) return false;
      const f = formRef.current;
      if (!f) return false;
      const hasData =
        !!f.customer_id ||
        (f.items && f.items.length > 0) ||
        (f.notes || "").trim() !== "" ||
        (f.payment_info || "").trim() !== "" ||
        (f.attachments && f.attachments.length > 0);
      return hasData;
    };

    // Tab close / hard reload — fire-and-forget via fetch(keepalive:true)
    const onBeforeUnload = () => {
      if (!shouldAutoSave()) return;
      const f = formRef.current;
      if (!f || !f.customer_id) return; // silent — user has left the tab
      const payload = buildDraftPayload();
      if (!payload) return;
      const token = localStorage.getItem("access_token") || "";
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
          credentials: "include",
          keepalive: true,
        });
      } catch { /* browser is unloading — nothing we can do */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (!shouldAutoSave()) return;
      const f = formRef.current;
      if (!f.customer_id) {
        toast.info("Taslak kaydedilemedi — müşteri seçilmemiş", { duration: 4000 });
        return;
      }
      const payload = buildDraftPayload();
      if (!payload) return;
      // In-app navigation — axios keeps the request alive across route change.
      api.post("/quotes", payload)
        .then(({ data }) => {
          toast.success(`Taslak olarak kaydedildi · ${data.quote_number}`, { duration: 5000 });
        })
        .catch((err) => {
          toast.error("Taslak kaydedilemedi: " + formatApiErrorDetail(err.response?.data?.detail));
        });
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const save = async () => {
    if (!form.customer_id) { toast.error("Müşteri seçin"); return; }
    if (form.items.length === 0) { toast.error("En az bir kalem ekleyin"); return; }
    const payload = {
      customer_id: form.customer_id,
      tax_rate: Number(form.tax_rate),
      notes: form.notes,
      payment_info: (form.payment_info || "").trim(),
      items: form.items.map((it) => ({
        id: it.id || undefined,
        product_id: it.product_id,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price),
        due_date: (it.due_date || "").trim() || null,
        notes: (it.notes || "").trim(),
      })),
      attachments: form.attachments.map((a) => (
        a.existing
          ? { keep_idx: a.idx }
          : { name: a.name, content_type: a.content_type, data_b64: a.data_b64 }
      )),
    };
    try {
      if (isOrderEdit) {
        await api.put(`/orders/${id}`, payload);
        savedRef.current = true;
        toast.success("Sipariş güncellendi — depo/üretim ekranı güncellendi");
        navigate("/siparisler");
      } else if (isEdit) {
        await api.put(`/quotes/${id}`, payload);
        savedRef.current = true;
        toast.success("Teklif güncellendi");
        navigate("/teklifler");
      } else {
        await api.post("/quotes", payload);
        savedRef.current = true;
        toast.success("Teklif oluşturuldu");
        navigate("/teklifler");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="quote-form-page">
      <div className="flex items-end justify-between border-b-2 border-[#09090B] pb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">
            {isOrderEdit ? "Sipariş Düzenle" : "Teklif Oluştur"}
          </div>
          <h1 className="text-4xl font-black tracking-tighter font-heading">
            {isOrderEdit ? "Siparişi Düzenle" : (isEdit ? "Teklifi Düzenle" : "Yeni Teklif")}
          </h1>
          {isOrderEdit && (
            <p className="text-sm text-[#FF3B30] mt-1 font-mono">
              Uyarı: kaydettiğinizde depo/üretim ilerlemesi sıfırlanır ve kalemler yeniden dağıtılır.
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => navigate(isOrderEdit ? "/siparisler" : "/teklifler")}>
            <ArrowLeft size={16} className="mr-2" /> Geri
          </Button>
          {!isEdit && !isOrderEdit && (
            <>
              <input
                type="file"
                ref={importInputRef}
                accept=".xlsx"
                className="hidden"
                onChange={(e) => handleImportFile(e.target.files)}
                data-testid="quote-import-input"
              />
              <Button
                variant="outline"
                className="rounded-none border-2 border-[#09090B]"
                onClick={downloadImportTemplate}
                data-testid="quote-import-template-btn"
                title="Excel şablonunu indir"
              >
                <FileSpreadsheet size={16} className="mr-2" /> Şablon
              </Button>
              <Button
                variant="outline"
                disabled={importing}
                onClick={() => importInputRef.current?.click()}
                className="rounded-none border-2 border-[#09090B] bg-[#F4F4F5]"
                data-testid="quote-import-btn"
                title="Excel dosyasından teklif kalemlerini içeri aktar"
              >
                <Upload size={16} className="mr-2" /> {importing ? "Aktarılıyor…" : "Excel'den Aktar"}
              </Button>
            </>
          )}
          {isEdit && !isOrderEdit && (
            <>
              <input type="file" ref={itemsInputRef} accept=".xlsx" className="hidden"
                onChange={(e) => handleItemsFile(e.target.files)} data-testid="quote-items-excel-input" />
              <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={downloadItemsExcel}
                data-testid="quote-items-excel-download-btn" title="Kalemleri Excel olarak indir">
                <Download size={16} className="mr-2" /> Excel İndir
              </Button>
              <Button variant="outline" disabled={itemsUploading} onClick={() => itemsInputRef.current?.click()}
                className="rounded-none border-2 border-[#09090B] bg-[#F4F4F5]" data-testid="quote-items-excel-upload-btn"
                title="Excel'den kalemleri güncelle">
                <Upload size={16} className="mr-2" /> {itemsUploading ? "Yükleniyor…" : "Excel Yükle"}
              </Button>
            </>
          )}
          <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={save} data-testid="save-quote-btn">
            <Save size={16} className="mr-2" /> Kaydet
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Customer + notes */}
        <div className="lg:col-span-2 bg-white border-2 border-[#09090B] p-6 space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase">Müşteri</Label>
            <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={customerOpen}
                  className="w-full h-11 justify-between rounded-none border-2 border-[#09090B] bg-white font-normal hover:bg-white focus-visible:ring-0"
                  data-testid="customer-select"
                >
                  {form.customer_id
                    ? (customers.find((c) => c.id === form.customer_id)?.name || "Müşteri seçin")
                    : <span className="text-neutral-400">Müşteri seçin…</span>}
                  <ChevronsUpDown size={16} className="opacity-60 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 rounded-none border-2 border-[#09090B] bg-white"
                style={{ width: "var(--radix-popover-trigger-width)" }}
                align="start"
              >
                <Command
                  filter={(value, search) => {
                    // value here is the SelectItem "value" prop → we set it to the customer name
                    // so cmdk's default fuzzy match works on the name.
                    return value.toLocaleLowerCase("tr-TR").includes(search.toLocaleLowerCase("tr-TR")) ? 1 : 0;
                  }}
                >
                  <CommandInput
                    placeholder="Müşteri ara (isim yazın)…"
                    className="h-10 font-mono"
                    data-testid="customer-search-input"
                  />
                  <CommandList className="max-h-72">
                    <CommandEmpty className="py-6 text-center text-sm text-neutral-500">Eşleşen müşteri yok</CommandEmpty>
                    <CommandGroup>
                      {customers.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => {
                            setForm((f) => ({
                              ...f,
                              customer_id: c.id,
                              // Müşterinin varsayılan ödeme bilgisi varsa ve
                              // teklifte henüz bir seçim yoksa otomatik doldur.
                              payment_info: (f.payment_info && f.payment_info.trim())
                                ? f.payment_info
                                : (c.default_payment_info || ""),
                            }));
                            setCustomerOpen(false);
                          }}
                          className="rounded-none cursor-pointer"
                          data-testid={`customer-opt-${c.id}`}
                        >
                          <Check
                            size={14}
                            className={`mr-2 ${form.customer_id === c.id ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="font-medium">{c.name}</span>
                          {c.email && <span className="ml-2 text-xs text-neutral-500 font-mono">{c.email}</span>}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Ödeme Bilgisi</Label>
            <Popover open={paymentOpen} onOpenChange={setPaymentOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={paymentOpen}
                  className="w-full h-11 justify-between rounded-none border-2 border-[#09090B] bg-white font-normal hover:bg-white focus-visible:ring-0"
                  data-testid="payment-info-select"
                >
                  {form.payment_info
                    ? <span className="truncate text-left">{form.payment_info}</span>
                    : <span className="text-neutral-400">Ödeme yöntemi seçin veya yazın…</span>}
                  <ChevronsUpDown size={16} className="opacity-60 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 rounded-none border-2 border-[#09090B] bg-white"
                style={{ width: "var(--radix-popover-trigger-width)" }}
                align="start"
              >
                <Command
                  filter={(value, search) =>
                    value.toLocaleLowerCase("tr-TR").includes(search.toLocaleLowerCase("tr-TR")) ? 1 : 0
                  }
                >
                  <CommandInput
                    placeholder="Ara veya yeni değer yazın…"
                    className="h-10 font-mono"
                    value={form.payment_info || ""}
                    onValueChange={(v) => setForm((f) => ({ ...f, payment_info: v }))}
                    data-testid="payment-info-search-input"
                  />
                  <CommandList className="max-h-64">
                    <CommandEmpty className="py-4 px-2 text-xs text-neutral-500">
                      Ayarlar'dan seçenek tanımlanmamış — istediğinizi yazıp kaydedebilirsiniz.
                    </CommandEmpty>
                    <CommandGroup>
                      {paymentOptions.map((opt) => (
                        <CommandItem
                          key={opt}
                          value={opt}
                          onSelect={() => {
                            setForm((f) => ({ ...f, payment_info: opt }));
                            setPaymentOpen(false);
                          }}
                          className="rounded-none cursor-pointer"
                          data-testid={`payment-info-opt-${opt}`}
                        >
                          <Check
                            size={14}
                            className={`mr-2 ${form.payment_info === opt ? "opacity-100" : "opacity-0"}`}
                          />
                          <span>{opt}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {form.payment_info && (
              <button
                type="button"
                className="mt-1 text-[10px] font-mono uppercase text-neutral-500 hover:text-[#FF3B30]"
                onClick={() => setForm((f) => ({ ...f, payment_info: "" }))}
                data-testid="payment-info-clear"
              >
                Temizle
              </button>
            )}
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Notlar</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" rows={3} data-testid="quote-notes" />
          </div>
        </div>

        {/* Totals */}
        <div className="bg-white border-2 border-[#09090B] p-6 space-y-3">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Toplam</div>
          <div className="flex justify-between font-mono"><span>Ara Toplam</span><span data-testid="subtotal">{fmtCurrency(subtotal)}</span></div>
          <div className="flex justify-between font-mono items-center">
            <span>KDV %</span>
            <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
              className="w-20 h-8 text-right rounded-none border-2 border-[#09090B] font-mono" data-testid="tax-rate-input" />
          </div>
          <div className="flex justify-between font-mono"><span>KDV Tutarı</span><span data-testid="tax-amount">{fmtCurrency(taxAmount)}</span></div>
          <div className="pt-3 border-t-2 border-[#09090B] flex justify-between text-xl font-mono font-bold"><span>Genel</span><span data-testid="grand-total">{fmtCurrency(total)}</span></div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white border-2 border-[#09090B]">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#09090B]">
          <div className="flex items-center gap-2"><Package size={18} /><span className="font-bold uppercase text-sm tracking-wider">Kalemler</span></div>
          <span className="text-xs font-mono text-neutral-500" data-testid="items-count-badge">{form.items.length} kalem</span>
        </div>
        <div className="divide-y-2 divide-neutral-200">
          {form.items.map((it, i) => {
            const p = productMap[it.product_id];
            const qty = Number(it.quantity) || 0;
            const stock = p ? Number(p.stock) || 0 : 0;
            const goesTo = p ? (stock >= qty ? "warehouse" : "production") : null;
            return (
              <div key={i} className="grid grid-cols-12 gap-3 p-4 items-end" data-testid={`quote-item-${i}`}>
                <div className="col-span-5">
                  <Label className="text-[10px] font-mono uppercase text-neutral-500">Ürün</Label>
                  <Popover open={openProductRow === i} onOpenChange={(v) => setOpenProductRow(v ? i : null)}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={openProductRow === i}
                        className="w-full h-10 justify-between rounded-none border-2 border-[#09090B] bg-white font-normal hover:bg-white focus-visible:ring-0"
                        data-testid={`product-select-${i}`}
                      >
                        <span className="truncate text-left">
                          {p
                            ? `${p.sku || "—"} · ${p.name} (Stok: ${p.stock})`
                            : <span className="text-neutral-400">Ürün ara veya seç…</span>}
                        </span>
                        <ChevronsUpDown size={16} className="opacity-60 shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0 rounded-none border-2 border-[#09090B] bg-white"
                      style={{ minWidth: "max(400px, var(--radix-popover-trigger-width))" }}
                      align="start"
                      sideOffset={4}
                      collisionPadding={16}
                      avoidCollisions
                    >
                      <div className="max-w-[92vw]" style={{ minWidth: 400 }}>
                      <Command
                        filter={(value, search) =>
                          value.toLocaleLowerCase("tr-TR").includes(search.toLocaleLowerCase("tr-TR")) ? 1 : 0
                        }
                      >
                        <CommandInput
                          placeholder="Ürün ara (SKU veya isim)…"
                          className="h-10 font-mono"
                          data-testid={`product-search-input-${i}`}
                        />
                        <CommandList className="max-h-72">
                          <CommandEmpty className="py-6 text-center text-sm text-neutral-500">Eşleşen ürün yok</CommandEmpty>
                          <CommandGroup>
                            {products.map((op) => (
                              <CommandItem
                                key={op.id}
                                value={`${op.sku || ""} ${op.name}`}
                                onSelect={() => {
                                  updateItem(i, { product_id: op.id });
                                  setOpenProductRow(null);
                                }}
                                className="rounded-none cursor-pointer items-start !h-auto min-h-[2.5rem] py-2"
                                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                                data-testid={`product-opt-${i}-${op.id}`}
                                title={`${op.sku ? op.sku + " · " : ""}${op.name} (Stok: ${op.stock})`}
                              >
                                <Check
                                  size={14}
                                  className={`mr-2 mt-1 shrink-0 ${it.product_id === op.id ? "opacity-100" : "opacity-0"}`}
                                />
                                <span className="font-mono text-xs text-neutral-500 mr-2 shrink-0 mt-0.5">{op.sku || "—"}</span>
                                <span
                                  className="font-medium flex-1 leading-snug"
                                  style={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
                                >
                                  {op.name}
                                </span>
                                <span className="ml-2 text-[10px] font-mono text-neutral-500 shrink-0 mt-0.5">Stok: {op.stock}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-mono uppercase text-neutral-500">Miktar</Label>
                  <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] font-mono" data-testid={`qty-input-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-mono uppercase text-neutral-500">Birim Fiyat</Label>
                  <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] font-mono" data-testid={`price-input-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-mono uppercase text-neutral-500">Toplam</Label>
                  <div className="h-10 flex items-center font-mono font-bold">{fmtCurrency(qty * (Number(it.unit_price) || 0))}</div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => removeItem(i)} data-testid={`remove-item-${i}`}><Trash2 size={14} className="text-red-600" /></Button>
                </div>
                {p && (
                  <div className="col-span-12 flex flex-wrap items-center gap-3">
                    {goesTo === "warehouse" ? (
                      <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-2 py-1 border-2 border-[#0055FF] text-[#0055FF]" data-testid={`route-badge-${i}`}>
                        <Warehouse size={14} /> Depo · Stok yeterli ({stock} {p.unit})
                      </div>
                    ) : (
                      <>
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-2 py-1 border-2 border-[#FF3B30] text-[#FF3B30]" data-testid={`route-badge-${i}`}>
                          <Factory size={14} /> Üretim · {Math.max(0, qty - stock)} {p.unit} üretilecek
                        </div>
                        <div className="inline-flex items-center gap-2">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">Termin</Label>
                          <Input
                            type="date"
                            value={it.due_date || ""}
                            onChange={(e) => updateItem(i, { due_date: e.target.value })}
                            className="h-8 w-40 rounded-none border-2 border-[#09090B] font-mono text-xs"
                            data-testid={`due-date-input-${i}`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="col-span-12">
                  <Label className="text-[10px] font-mono uppercase text-neutral-500">Açıklama (opsiyonel · PDF'te bu satırın altına yazılır)</Label>
                  <Textarea
                    value={it.notes || ""}
                    onChange={(e) => updateItem(i, { notes: e.target.value })}
                    rows={2}
                    placeholder="Ör. özel renk, teslim koşulu, teknik detay…"
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 text-sm"
                    data-testid={`item-notes-${i}`}
                  />
                </div>
              </div>
            );
          })}
          {form.items.length === 0 && (
            <div className="p-8 text-center text-neutral-500 text-sm">Henüz kalem eklenmedi.</div>
          )}
        </div>
        {/* Sticky Add Item bar - always at the bottom of the items list, scrolls with the content */}
        <div className="border-t-2 border-[#09090B] bg-[#F4F4F5] p-3 flex justify-end">
          <Button
            size="sm"
            onClick={addItem}
            data-testid="add-item-btn"
            className="rounded-none bg-[#09090B] hover:bg-[#0055FF] h-10 px-4 font-bold uppercase tracking-widest text-xs"
          >
            <Plus size={14} className="mr-1" /> Kalem Ekle
          </Button>
        </div>
      </div>

      {/* Attachments */}
      <div className="bg-white border-2 border-[#09090B]" data-testid="attachments-section">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#09090B]">
          <div className="flex items-center gap-2">
            <Paperclip size={18} />
            <span className="font-bold uppercase text-sm tracking-wider">Ekler</span>
            <span className="text-[10px] font-mono uppercase text-neutral-500 tracking-widest">
              PDF · PNG · JPG · Max 5 dosya · Her biri max 5 MB
            </span>
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              multiple
              className="hidden"
              onChange={(e) => { addAttachments(e.target.files); e.target.value = ""; }}
              data-testid="attachment-input"
            />
            <span className="inline-flex items-center gap-2 px-3 h-9 bg-[#09090B] hover:bg-[#0055FF] text-white text-sm rounded-none">
              <Plus size={14} /> Dosya Ekle
            </span>
          </label>
        </div>
        {form.attachments.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 text-sm">Henüz dosya eklenmedi.</div>
        ) : (
          <ul className="divide-y-2 divide-neutral-200">
            {form.attachments.map((a, idx) => (
              <li key={idx} className="flex items-center gap-3 p-3" data-testid={`attachment-row-${idx}`}>
                {a.content_type === "application/pdf" ? <FileTextIcon size={18} className="text-[#FF3B30] shrink-0" /> : <FileImage size={18} className="text-[#0055FF] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{a.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                    {a.content_type} · {a.size ? `${(a.size / 1024).toFixed(1)} KB` : "-"}
                    {a.existing && <span className="ml-2 text-[#0055FF]">MEVCUT</span>}
                  </div>
                </div>
                {a.existing && (
                  <Button variant="ghost" size="sm" className="rounded-none" onClick={() => downloadExisting(a)} title="İndir" data-testid={`attachment-download-${idx}`}>
                    <Download size={14} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="rounded-none" onClick={() => removeAttachment(idx)} title="Kaldır" data-testid={`attachment-remove-${idx}`}>
                  <XIcon size={14} className="text-red-600" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
